// need importing them explicitly otherwise esbuild will treeshake them
import './Tracker';
import './ReachPlayerNormalizer';
import './HeartbeatNormalizer';
import './PlayStateNormalizer';
import './InteractionNormalizer';
import './AdsNormalizer';

import videojs, { VideoJsPlayer } from 'video.js';

import { attachPluginToPlayer } from '@src/utils/plugin';

import AdsNormalizer, { AdsNormalizerFactoryFn } from './AdsNormalizer';
import HeartbeatNormalizer, {
  HeartbeatNormalizerFactoryFn,
} from './HeartbeatNormalizer';
import InteractionNormalizer, {
  InteractionNormalizerFactoryFn,
} from './InteractionNormalizer';
import PlayStateNormalizer, {
  PlayStateNormalizerFactoryFn,
} from './PlayStateNormalizer';
import ReachPlayerNormalizer, {
  ReachPlayerNormalizerFactoryFn,
} from './ReachPlayerNormalizer';
import Tracker, { TrackerFactoryFn } from './Tracker';

const Plugin = videojs.getPlugin('plugin');

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface Options {}

const PLUGIN_NAME = 'eventManagerPlugin';

class EventManagerPlugin extends Plugin {
  tracker?: Tracker;
  reachPlayerNormalizer?: ReachPlayerNormalizer;
  heartbeatNormalizer?: HeartbeatNormalizer;
  playStateNormalizer?: PlayStateNormalizer;
  interactionNormalizer?: InteractionNormalizer;
  adsNormalizer?: AdsNormalizer;
  vjsPlayer: VideoJsPlayer;
  options?: Options;

  constructor(player: VideoJsPlayer, options?: Options) {
    super(player, options);

    this.vjsPlayer = player;
    this.options = options;

    attachPluginToPlayer(this, player);

    this.vjsPlayer.on('pluginsetup:eventManagerPlugin', this.init);
  }

  init = () => {
    // the order of initialization matters, e.g. tracker need to be initialized first
    if (!this.player.usingPlugin('eventManagerTracker')) {
      this.tracker = (
        this.player.eventManagerTracker as unknown as TrackerFactoryFn
      )(this.options);
    }
    if (!this.player.usingPlugin('eventManagerReachPlayerNormalizer')) {
      this.reachPlayerNormalizer = (
        this.player
          .eventManagerReachPlayerNormalizer as unknown as ReachPlayerNormalizerFactoryFn
      )(this.options);
    }
    if (!this.player.usingPlugin('eventManagerHeartbeatNormalizer')) {
      this.heartbeatNormalizer = (
        this.player
          .eventManagerHeartbeatNormalizer as unknown as HeartbeatNormalizerFactoryFn
      )(this.options);
    }
    if (!this.player.usingPlugin('eventManagerPlayStateNormalizer')) {
      this.playStateNormalizer = (
        this.player
          .eventManagerPlayStateNormalizer as unknown as PlayStateNormalizerFactoryFn
      )(this.options);
    }
    if (!this.player.usingPlugin('eventManagerInteractionNormalizer')) {
      this.interactionNormalizer = (
        this.player
          .eventManagerInteractionNormalizer as unknown as InteractionNormalizerFactoryFn
      )(this.options);
    }
    if (!this.player.usingPlugin('eventManagerAdsNormalizer')) {
      this.adsNormalizer = (
        this.player
          .eventManagerAdsNormalizer as unknown as AdsNormalizerFactoryFn
      )(this.options);
    }
  };

  resetStateForNextSource() {
    this.playStateNormalizer?.resetState();
    this.heartbeatNormalizer?.disposePlayStateListeners();
    this.heartbeatNormalizer?.disposePlayStateListeners();
  }

  dispose() {
    if (!this.player) return; // when player is already disposed
    this.vjsPlayer.off('pluginsetup:eventManagerPlugin', this.init);
    if (this.player) {
      this.tracker?.dispose();
      this.reachPlayerNormalizer?.dispose();
      this.heartbeatNormalizer?.dispose();
      this.playStateNormalizer?.dispose();
      this.interactionNormalizer?.dispose();
      this.adsNormalizer?.dispose();
    }
    super.dispose();
  }
}

videojs.registerPlugin(PLUGIN_NAME, EventManagerPlugin);

export type EventManagerPluginFactoryFn = (
  options?: Options
) => EventManagerPlugin;

export default EventManagerPlugin;
